import React, { useState, useEffect, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

//Redux
import { useDispatch, useSelector } from "react-redux";

//Components
import Layout from "../../../Layout/Layout";
import Constants from "../../../common/SupplierConstants";
import { SearchField } from "../../../Components/formElements/SearchField";
import ShowStatus from "../../../Components/Modal/supplier_app/Workflow/ShowStatus";

//Redux - company slice
import {
    getCompanyList,
    deleteCompany,
} from "../../../redux/feature/supplier_app/companySlice";

const CompanyList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const companyData = useSelector((state) => state?.company?.data);
    const companyPagination = useSelector((state) => state?.company?.pagination);
    const isLoading = useSelector((state) => state?.company?.status);

    const [getPage, setPage] = useState(1);
    const [getNewPerPage, setNewPerPage] = useState(10);
    const [getCommonSearchTxt, setCommonSearchTxt] = useState("");

    // Workflow log modal - same ShowStatus used across the application
    const [modalShowStatus, setModalShowStatus] = useState(false);
    const [sendDataModal, setSendDataModal] = useState([]);
    const [modelTitle, setModelTitle] = useState("");

    const showStatusInfo = (rowData) => {
        setModalShowStatus(true);
        setSendDataModal(rowData);
        setModelTitle("Status Information");
    };
    const hideModalStatus = () => {
        setModalShowStatus(false);
    };

    // Accordion card visibility (same as PR List / RFQ create)
    const [isCardVisible, setCardVisible] = useState(true);
    const toggleCardVisibility = () => setCardVisible(!isCardVisible);

    useEffect(() => {
        loadCompanyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getPage, getNewPerPage]);

    const loadCompanyList = () => {
        let obj = {
            page: getPage,
            per_page: getNewPerPage,
            searchTerm: getCommonSearchTxt,
        };
        dispatch(getCompanyList(obj));
    };

    const handlePageChange = (page) => {
        setPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setNewPerPage(newPerPage);
        setPage(page);
    };

    // Search - trigger on search icon click
    const handleSearchFuncApi = () => {
        setPage(1);
        let obj = {
            page: 1,
            per_page: getNewPerPage,
            searchTerm: getCommonSearchTxt,
        };
        dispatch(getCompanyList(obj));
    };

    const handleView = (row) => {
        navigate(Constants.COMPANY_CREATION_DETAILS, { state: { companyId: row._id } });
    };

    const handleEdit = (row) => {
        navigate(Constants.COMPANY_CREATION_UPDATE, { state: { companyId: row._id } });
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Are you sure you want to delete Company - ${row.documentNumber || row.companyCode}?`)) return;
        try {
            await dispatch(deleteCompany({ _id: row._id })).unwrap();
            toast.success("Company deleted successfully");
            loadCompanyList();
        } catch (error) {
            console.log("deleteCompany Failed:", error);
            toast.error("!! Unable to delete Company");
        }
    };

    const handleAddCompany = () => {
        navigate(Constants.COMPANY_CREATION_CREATE);
    };

    const columns = useCallback(
        () => [
            {
                name: "S.No",
                selector: (row, index) => (getPage - 1) * getNewPerPage + index + 1,
                width: "70px",
            },
            {
                name: "Document Number",
                cell: (row) => (
                    <button
                        className="btnInfo"
                        onClick={() => handleView(row)}
                    >
                        {row.documentNumber || row.companyCode}{" "}
                    </button>
                ),
                width: "160px",
            },
            {
                name: "Company Code",
                selector: (row) => row.companyCode || "-",
                sortable: true,
            },
            {
                name: "Company Name",
                selector: (row) => row.companyName,
                sortable: true,
            },
            {
                name: "Legal Name",
                selector: (row) => row.legalName || "-",
                sortable: true,
            },
            {
                name: "Company Type",
                selector: (row) => row.companyType || "-",
                sortable: true,
            },
            {
                name: "City",
                selector: (row) => row.city || "-",
            },
            {
                name: "GSTIN",
                selector: (row) => row.gstNumber || "-",
            },
            // Workflow log - same column used across the application
            ...(companyData?.some((row) => row.workflowActionLogs !== undefined)
                ? [
                    {
                        name: "Workflow log",
                        selector: (row) => row.workflowActionLogs,
                        cell: (row) => (
                            <div
                                style={{ width: "100%" }}
                                className="d-flex justify-content-center iconTable"
                            >
                                <img
                                    src="assets/img/newIcons/workflow.png"
                                    alt="workflow"
                                    style={{ width: "35px" }}
                                    className="mt-1"
                                    onClick={() => showStatusInfo(row.workflowActionLogs)}
                                />
                            </div>
                        ),
                        sortable: true,
                        width: "132px",
                    },
                ]
                : []),
            {
                name: "Status",
                selector: (row) => row.companyStatus || "-",
                cell: (row) => (
                    <span
                        className={`badge ${row.companyStatus === "Active" ? "bg-success" : "bg-danger"}`}
                    >
                        {row.companyStatus || "-"}
                    </span>
                ),
            },
            {
                name: "Actions",
                cell: (row) => (
                    <div className="d-flex gap-1">
                        <button
                            className="btnTable"
                            title="View Details"
                            onClick={() => handleView(row)}
                        >
                            <i className="bi bi-eye"></i>
                        </button>
                        <button
                            className="btnTable"
                            title="Edit"
                            onClick={() => handleEdit(row)}
                        >
                            <i className="bi bi-pencil-square"></i>
                        </button>
                        <button
                            className="btnTable"
                            title="Delete"
                            onClick={() => handleDelete(row)}
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                ),
                ignoreRowClick: true,
                allowOverflow: true,
                button: true,
                width: "140px",
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getPage, getNewPerPage, companyData]
    );

    const customStyles = {
        headCells: {
            style: {
                fontWeight: "bold",
                backgroundColor: "#f3f4f6",
            },
        },
    };

    return (
        <main className="dashboard main" id="main">
            <Layout />
            <Row className="justify-center subContent">
                <Col xs={12} md={12} className="rightSide">
                    <div className="rightSideInner PageCommonCSS">
                        <h2 className="mainTitle">Company List</h2>
                        <p className="subText">Company Creation and Details</p>
                        <hr />

                        {/* Search Row */}
                        <Row className="mb-3 align-items-center">
                            <Col xs={12} md={8} lg={9}></Col>
                            <Col xs={10} md={3} lg={3} className="d-flex justify-content-end">
                                <SearchField
                                    name="searchKey"
                                    placeholder="Search by Document Number / Code / Name / GSTIN"
                                    value={getCommonSearchTxt}
                                    onChange={(event) => setCommonSearchTxt(event.target.value)}
                                    onClick={() => handleSearchFuncApi()}
                                />
                            </Col>
                        </Row>

                        {/* Company List Accordion */}
                        <div className="accordion" id="accordionExample">
                            <div className="accordion-item">
                                <h2 className="accordion-header">
                                    <Row className="accordion-button">
                                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                                            <i
                                                className={`bi ${isCardVisible ? "bi-chevron-right" : "bi-chevron-down"}`}
                                                onClick={toggleCardVisibility}
                                            ></i>
                                        </Col>
                                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                                            <span className="iconLogo">CO</span>
                                        </Col>
                                        <Col xs={8} md={4} lg={4}>
                                            <span className="headerTitle">Company List</span>
                                            <span className="subHeaderTitle">
                                                View of Companies and Status
                                            </span>
                                        </Col>

                                        <Col xs={12} md={6} lg={6} className="headerButtonAlign">
                                            <Col xs={6} md={4} lg={4} className="">
                                                <button
                                                    className="btnTable"
                                                    onClick={handleAddCompany}
                                                >
                                                    <i className="bi bi-plus-lg"></i> Create Company
                                                </button>
                                            </Col>
                                        </Col>
                                    </Row>
                                </h2>

                                <div
                                    className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}
                                >
                                    <div className="accordion-body">
                                        <Row>
                                            <Col xs={12} md={12} lg={12}>
                                                <DataTable
                                                    columns={columns()}
                                                    data={companyData || []}
                                                    progressPending={isLoading}
                                                    customStyles={customStyles}
                                                    pagination
                                                    paginationServer
                                                    paginationTotalRows={companyPagination?.total || 0}
                                                    paginationDefaultPage={getPage}
                                                    paginationPerPage={getNewPerPage}
                                                    paginationRowsPerPageOptions={Constants.ROW_PER_PAGE}
                                                    onChangePage={handlePageChange}
                                                    onChangeRowsPerPage={handlePerRowsChange}
                                                    highlightOnHover
                                                    pointerOnHover
                                                    noDataComponent={
                                                        <div className="noDataMsg">No Companies Found</div>
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            <ShowStatus
                show={modalShowStatus}
                onHide={() => hideModalStatus()}
                rowdatamodal={sendDataModal}
                modeltitle={modelTitle}
            />
        </main>
    );
};

export default CompanyList;
